/*
 * Copyright (C) 20019 FlexLink
 * Original CPP Code Copyright (C) 2007-2016 Rafael Durbano Lobato
 *
 * This file is part of Recursive Partitioning Algorithm.
 *
 * Recursive Partitioning Algorithm is free software: you can
 * redistribute it and/or modify it under the terms of the GNU General
 * Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * Recursive Partitioning Algorithm is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR
 * PURPOSE. See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with Recursive Partitioning Algorithm. If not, see
 * <http://www.gnu.org/licenses/>.
 *
 * Contact information:
 *
 * Rafael Durbano Lobato <lobato@ime.usp.br>
 * http://www.ime.usp.br/~lobato/
 */

/**
 * ApContext.normalize[x] returns the value of <x>_S (the greatest element of S
 * less than or equal to x):
 *
 * <x>_S = max {s | s \in S, s <= x}.
 */
import { ApContext } from './apcontext'

/**
 * Insert an element into the specified set, in the case it does not
 * belong to the set yet.
 *
 * Parameters:
 * element - The element to be inseted.
 * set - The set where the element will be inserted.
 *
 * Return:
 * The size of the set after this insertion.
 */
// int insert(int element, Set set) {
function insert (element, set) {
  if (set.size === 0 ||
     set.points[set.size - 1] < element) {
    set.points[set.size] = element
    return set.size + 1
  } else {
    return set.size
  }
}

// struct Set {
//   int size;
//   int *points;
// };
/* Set newSet(int maxSize); */
export function newSet (maxSize) {
  // JS: have to use original set semantics - ordered array + size property.
  return { size: 0, points: new Array(maxSize).fill(0) }
}

/******************************************************************
 ******************************************************************/
/**
 * Construct the raster points sets X' and Y', which are defined as
 *
 * X' = {<L - x>_X | x \in X} U {0}
 * Y' = {<W - y>_Y | y \in Y} U {0}
 *
 * where X and Y are the integer conic combinations sets for L and W,
 * respectively, and <s'>_S = max {s | s \in S, s <= s'}.
 *
 * Parameters:
 * L                 - Length of the rectangle.
 * W                 - Width of the rectangle.
 * rasterPointsX     - Pointer to the raster points set X'.
 * rasterPointsY     - Pointer to the raster points set Y'.
 * conicCombinations - Set of integer conic combinations of l and w.
 *
 * Remark: it suposes that the integer conic combinations set is
 * sorted.
 * void constructRasterPoints(int L, int W, Set *rasterPointsX,
 *                            Set *rasterPointsY, Set conicCombinations);
 * JS: returns object {rasterPointsX, rasterPointsY}
 */
export function constructRasterPoints (L, W, conicCombinations) {
  /* Maximum raster points X size. */
  let xSize = conicCombinations.size
  // subtract from xSize number of elements > L
  // for(let i = xSize - 1; i >= 0 && conicCombinations.points[i] > L; i--)
  //   xSize--;
  conicCombinations.points.forEach(el => {
    if (el > L) xSize--
  })

  /* Maximum raster points Y size. */
  let ySize = conicCombinations.size
  // subtract from ySize number of elements > W
  // for(let i = ySize - 1; i >= 0 && conicCombinations.points[i] > W; i--)
  //   ySize--;
  //
  conicCombinations.points.forEach(el => {
    if (el > W) ySize--
  })

  const rasterPointsX = newSet(xSize + 2)
  const rasterPointsY = newSet(ySize + 2)

  /* Construct the raster points for L. */
  for (let i = xSize - 1; i >= 0; i--) {
    const x = ApContext.normalize[L - conicCombinations.points[i]]
    rasterPointsX.size = insert(x, rasterPointsX)
  }

  /* Construct the raster points for W. */
  for (let i = ySize - 1; i >= 0; i--) {
    const y = ApContext.normalize[W - conicCombinations.points[i]]
    rasterPointsY.size = insert(y, rasterPointsY)
  }

  return { rasterPointsX, rasterPointsY }
}

/**
 * Construct the set X of integer conic combinations of l and w.
 *
 * X = {x | x = rl + sw, x <= L, r,s >= 0 integers}
 *
 * Parameters:
 * L - Length of the rectangle.
 * l - Length of the boxes to be packed.
 * w - Width of the boxes to be packed.
 * X - Pointer to the set X.
 *
 * void constructConicCombinations(int L, int l, int w, Set *X);
 * JS: returns new set X.
 */
export function constructConicCombinations (L, l, w, X) {
  // int * inX = (int *) calloc(L + 2, sizeof(int));
  const inX = new Array(L + 2).fill(0)
  // int * c   = (int *) calloc(L + 2, sizeof(int));
  const c = new Array(L + 2).fill(0)

  X.points[0] = 0
  X.size = 1
  inX[0] = 1

  for (let i = 0; i <= L; i++) {
    c[i] = 0
  }

  for (let i = l; i <= L; i++) {
    if (c[i] < c[i - l] + l) {
      c[i] = c[i - l] + l
    }
  }

  for (let i = w; i <= L; i++) {
    if (c[i] < c[i - w] + w) {
      c[i] = c[i - w] + w
    }
  }

  for (let i = 1; i <= L; i++) {
    if (c[i] === i && inX[i] === 0) {
      X.points[X.size] = i
      X.size++
      inX[i] = 1
    }
  }

  if (X.points[X.size - 1] !== L) {
    /* Insert L int the set X. */
    X.points[X.size] = L
    X.size++
  }
}
